import * as React from 'react';
import classNames from 'classnames';

import {
  DropdownPopover,
  Input,
  Outside,
  IconSearch16,
  IconSuccess16,
  Tooltip,
  UIHeading5,
  UIText2,
} from '@cian/ui-kit';

import { IRoads, IRoadSuggest } from '../../../store/roads';
import { isArrayWithItems } from '../../../utils';
import { getRoadFullName } from '../../../utils/geo';
import { trackRoadSearchKeypress, trackSuggestChosen } from '../tracking';
import { getSuggestLinkText } from '../../filters/geo_textfield/getSuggestLinkText';

import * as styles from './RoadsSearch.css';
import { TOOLTIP_NO_OFFER_TEXT } from '../../../constants';

interface IRoadsSearchProps {
  roads: IRoads;

  onSelectRoad(id: number): void;
  onInputChange(value: string, trackNotFound?: boolean): void;
}

interface IRoadsSearchState {
  isLoading: boolean;
  isResultsVisible: boolean;
  query: string;
}

export class RoadsSearch extends React.Component<IRoadsSearchProps, IRoadsSearchState> {
  public state = {
    isLoading: false,
    isResultsVisible: false,
    query: '',
  };

  // Флажок для отправки аналитики только при первом нажатии на клавишу ввода поискового запроса
  private shouldSendAnalytics = true;

  public render() {
    return (
      <Outside onOutside={this.handleBlur}>
        <div className={styles['container']}>
          <DropdownPopover
            open={this.state.isResultsVisible && !!this.query}
            content={<div className={styles['list-container']}>{this.renderSuggestions()}</div>}
          >
            <Input
              leftAdornment={
                <div className={styles['search-icon']}>
                  <IconSearch16 display="block" color="primary_100" />
                </div>
              }
              placeholder="Название шоссе"
              value={this.state.query}
              onChange={this.handleInputChange}
              onFocus={this.handleFocus}
            />
          </DropdownPopover>
        </div>
      </Outside>
    );
  }
  private handleInputChange = (_e: React.ChangeEvent<HTMLInputElement>, query: string) => {
    if (this.shouldSendAnalytics) {
      trackRoadSearchKeypress();

      // Отправляем только в первый раз
      this.shouldSendAnalytics = false;
    }

    this.setState({ query }, () => {
      if (this.query) {
        this.props.onInputChange(this.query, true);
      }
    });
  };

  private get query() {
    const result = this.state.query.trim();

    return result.length >= 2 ? result : '';
  }

  private renderSuggestions = () => {
    const {
      roads: { suggests = [] },
    } = this.props;
    if (!isArrayWithItems(suggests)) {
      return this.renderNothingFound();
    }

    return (
      <ul role="listbox" className={styles['list']}>
        {suggests.map(suggest => (
          <li
            role="option"
            key={`suggestion-${suggest.id}`}
            className={classNames(styles['list-item'], { [styles['list-item--disabled']]: !suggest.isEnabled })}
            onClick={this.handleSelectSuggest(suggest)}
          >
            <div>
              {suggest.isActive && (
                <div className={styles['list-item_icon']}>
                  <IconSuccess16 color="primary_100" />
                </div>
              )}
              {suggest.isEnabled ? (
                getSuggestLinkText(getRoadFullName(suggest), this.query)
              ) : (
                <Tooltip title={TOOLTIP_NO_OFFER_TEXT} placement="right">
                  <span>{getSuggestLinkText(getRoadFullName(suggest), this.query)}</span>
                </Tooltip>
              )}
            </div>
          </li>
        ))}
      </ul>
    );
  };

  private renderNothingFound = () => (
    <div className={styles['empty']}>
      <div className={styles['empty-title']}>
        <UIHeading5>Ничего не найдено</UIHeading5>
      </div>
      <UIText2>Укажите другое шоссе</UIText2>
    </div>
  );

  private handleSelectSuggest =
    ({ id, isEnabled }: IRoadSuggest) =>
    () => {
      if (isEnabled) {
        trackSuggestChosen();
        this.setState(
          {
            isResultsVisible: false,
            query: '',
          },
          () => this.props.onSelectRoad(id),
        );
      }
    };

  private handleBlur = () => this.setState({ isResultsVisible: false });

  private handleFocus = () => {
    this.setState({ isResultsVisible: true });
    this.props.onInputChange(this.query);
  };
}
